// src/pages/KarigarManagement/Job/JobAssignmentPage.jsx
// UPDATED - Aligned with new backend API

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useJobManagement, useKarigarManagement } from "@/hooks/useKarigar";
import { Button } from "@/components/ui-v2/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import { Badge } from "@/components/ui-v2/badge";
import { Input } from "@/components/ui-v2/input";
import { Label } from "@/components/ui-v2/label";
import { Textarea } from "@/components/ui-v2/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui-v2/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui-v2/dialog";
import {
  ArrowLeft,
  Star,
  Clock,
  CheckCircle,
  Calendar,
  User,
  Award,
  AlertTriangle,
  Sparkles,
  Users,
  RefreshCw,
} from "lucide-react";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { toast } from "sonner";
import { format } from "date-fns";

const JobAssignmentPage = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();

  const {
    currentJob,
    loading: jobLoading,
    error: jobError,
    successMessage,
    productKarigars, // Product's capable karigars (shown FIRST)
    otherKarigars, // Other matching karigars (shown AFTER)
    allKarigars,
    recommendations,
    availableKarigars,
    getJobById,
    getAvailableKarigars,
    assign,
    clearError,
    clearSuccessMessage,
    clearAvailableKarigars,
  } = useJobManagement();

  const { getAvailability } = useKarigarManagement();

  const [selectedKarigar, setSelectedKarigar] = useState(null);
  const [expectedDate, setExpectedDate] = useState("");
  const [notes, setNotes] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [fetchingKarigars, setFetchingKarigars] = useState(false);

  // Fetch job details
  useEffect(() => {
    if (jobId) {
      console.log("[JobAssignment] Fetching job:", jobId);
      getJobById(jobId);
    }
    return () => {
      clearAvailableKarigars();
    };
  }, [jobId]);

  // ✅ FIXED: Check for both product and productSnapshot
  // Also handle jobs without product info
  useEffect(() => {
    if (!currentJob) {
      console.log("[JobAssignment] No currentJob yet");
      return;
    }

    console.log("[JobAssignment] currentJob loaded:", currentJob);

    // Get product info from either product or productSnapshot
    const product = currentJob.product || currentJob.productSnapshot;

    console.log("[JobAssignment] Product data:", product);

    // If we have product info, fetch available karigars
    if (product && product.type) {
      fetchAvailableKarigarsForJob(product);
    } else {
      // No product info - fetch all available karigars for this branch
      console.log(
        "[JobAssignment] No product info, fetching all karigars for branch"
      );
      fetchAvailableKarigarsForJob(null);
    }
  }, [currentJob]);

  // Function to fetch available karigars
  const fetchAvailableKarigarsForJob = async (product) => {
    setFetchingKarigars(true);

    const requestData = {
      // Product info (if available)
      productType: product?.type || null,
      productSubType: product?.subType || null,

      // Branch info
      branchCode:
        product?.branchInfo?.primaryBranch || currentJob?.branchCode || null,
      availableBranches: product?.branchInfo?.availableBranches || [],

      // Product's capable karigars (will be shown first!)
      capableKarigars:
        product?.manufacturing?.capableKarigars ||
        currentJob?.capableKarigars ||
        [],

      // Product's estimated time
      estimatedMakingTime: product?.manufacturing?.estimatedMakingTime || null,

      // Work details
      workType: currentJob?.jobType || null,
      complexity: currentJob?.complexity || "medium",
      requiredSkills: product?.manufacturing?.requiredSkills || [],

      // Product details for estimation
      grossWeight:
        product?.grossWeight ||
        currentJob?.specifications?.targetWeight ||
        null,
      netWeight: product?.netWeight || null,
      hasStones: product?.hasStones || false,

      // Target date
      targetDate: currentJob?.timeline?.expectedCompletionDate || null,

      // Include other karigars
      includeOtherKarigars: true,
    };

    console.log(
      "[JobAssignment] Fetching available karigars with:",
      requestData
    );

    try {
      await getAvailableKarigars(requestData);
    } catch (error) {
      console.error("[JobAssignment] Error fetching karigars:", error);
    } finally {
      setFetchingKarigars(false);
    }
  };

  // Handle success/error messages
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      clearSuccessMessage();
      navigate(`/karigar-management/jobs/${jobId}`);
    }
    if (jobError) {
      toast.error(jobError);
      clearError();
    }
  }, [successMessage, jobError]);

  // Set default expected date from estimation
  useEffect(() => {
    if (selectedKarigar?.estimation?.estimatedCompletionDate) {
      setExpectedDate(
        format(
          new Date(selectedKarigar.estimation.estimatedCompletionDate),
          "yyyy-MM-dd"
        )
      );
    }
  }, [selectedKarigar]);

  // ✅ FIXED: Handle assignment with correct function signature
  const handleAssign = async () => {
    if (!selectedKarigar) {
      toast.error("Please select a karigar");
      return;
    }

    setAssigning(true);
    try {
      // ✅ FIXED: Using correct assign function signature
      // assign(jobId, karigarId, reason) - expectedDate is handled separately
      const assignmentNotes = notes || `Expected completion: ${expectedDate}`;
      await assign(jobId, selectedKarigar.karigarId, assignmentNotes);
    } finally {
      setAssigning(false);
      setShowConfirmDialog(false);
    }
  };

  // Refresh karigars
  const handleRefreshKarigars = () => {
    const product = currentJob?.product || currentJob?.productSnapshot;
    fetchAvailableKarigarsForJob(product);
  };

  // Get product for display
  const getProductForDisplay = () => {
    return currentJob?.product || currentJob?.productSnapshot || {};
  };

  // Render karigar card
  const renderKarigarCard = (karigar, isProductKarigar = false) => {
    const isSelected = selectedKarigar?.karigarId === karigar.karigarId;

    return (
      <Card
        key={karigar.karigarId}
        className={`cursor-pointer transition-all ${
          isSelected
            ? "border-primary ring-2 ring-primary"
            : "hover:border-primary/50"
        } ${!karigar.availability?.isAvailable ? "opacity-60" : ""}`}
        onClick={() => {
          if (karigar.availability?.isAvailable) {
            setSelectedKarigar(karigar);
          }
        }}
      >
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            {/* Karigar Info */}
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-lg font-medium">
                  {karigar.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{karigar.name}</p>

                  {/* Product Karigar Badge */}
                  {isProductKarigar && (
                    <Badge className="bg-amber-100 text-amber-800">
                      <Sparkles className="h-3 w-3 mr-1" />
                      {karigar.productAssociation?.role || "CAPABLE"}
                    </Badge>
                  )}

                  {/* Primary Badge */}
                  {karigar.productAssociation?.role === "PRIMARY" && (
                    <Badge className="bg-green-100 text-green-800">
                      <Award className="h-3 w-3 mr-1" />
                      Primary
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {karigar.karigarCode}
                </p>

                {/* Product-specific stats */}
                {isProductKarigar &&
                  karigar.productAssociation?.productTypeStats && (
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span>
                        Made{" "}
                        {karigar.productAssociation.productTypeStats.totalMade}x
                      </span>
                      {karigar.productAssociation.productTypeStats
                        .qualityRating && (
                        <span className="flex items-center">
                          <Star className="h-3 w-3 text-yellow-500 mr-0.5" />
                          {karigar.productAssociation.productTypeStats.qualityRating.toFixed(
                            1
                          )}
                        </span>
                      )}
                    </div>
                  )}
              </div>
            </div>

            {/* Score */}
            <div className="text-right">
              <div className="text-2xl font-bold text-primary">
                {karigar.availabilityScore}
              </div>
              <p className="text-xs text-muted-foreground">Score</p>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
            {/* Rating */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Star className="h-4 w-4 text-yellow-500" />
                <span className="font-medium">
                  {karigar.performance?.overallRating?.toFixed(1) || "0.0"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">Rating</p>
            </div>

            {/* Workload */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <User className="h-4 w-4 text-blue-500" />
                <span className="font-medium">
                  {karigar.workload?.currentJobs}/{karigar.workload?.maxJobs}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">Jobs</p>
            </div>

            {/* Estimated Time */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Clock className="h-4 w-4 text-purple-500" />
                <span className="font-medium">
                  {karigar.estimation?.estimatedDays || "-"}d
                </span>
              </div>
              <p className="text-xs text-muted-foreground">Est. Time</p>
            </div>
          </div>

          {/* Availability Info */}
          <div className="mt-3 pt-3 border-t">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Available from:</span>
              <span className="font-medium">
                {karigar.availability?.nextAvailableDate
                  ? format(
                      new Date(karigar.availability.nextAvailableDate),
                      "dd MMM"
                    )
                  : "Now"}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-muted-foreground">Est. completion:</span>
              <span
                className={`font-medium ${
                  karigar.estimation?.canMeetTargetDate
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {karigar.estimation?.estimatedCompletionDate
                  ? format(
                      new Date(karigar.estimation.estimatedCompletionDate),
                      "dd MMM"
                    )
                  : "-"}
                {karigar.estimation?.canMeetTargetDate && (
                  <CheckCircle className="h-3 w-3 inline ml-1" />
                )}
              </span>
            </div>
          </div>

          {/* Not Available Warning */}
          {!karigar.availability?.isAvailable && (
            <div className="mt-3 p-2 bg-red-50 rounded text-sm text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {karigar.availability?.isInUnavailablePeriod
                ? "On leave"
                : "At full capacity"}
            </div>
          )}

          {/* Extra Availability */}
          {karigar.availability?.hasAvailableWindow && (
            <div className="mt-3 p-2 bg-green-50 rounded text-sm text-green-600 flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Has extra availability!
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Loading state
  if (jobLoading.detail) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  const product = getProductForDisplay();

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Assign Karigar</h1>
          <p className="text-muted-foreground">
            Job: {currentJob?.jobNumber} - {currentJob?.title}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefreshKarigars}
          disabled={fetchingKarigars || jobLoading.availableKarigars}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${fetchingKarigars ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {/* Job Summary */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Product</p>
              <p className="font-medium">
                {product?.name || currentJob?.title || "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Type</p>
              <p className="font-medium">
                {product?.type
                  ? `${product.type} - ${product?.subType || ""}`
                  : "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Work Type</p>
              <p className="font-medium">
                {currentJob?.jobType?.replace(/_/g, " ") || "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Target Date</p>
              <p className="font-medium">
                {currentJob?.timeline?.expectedCompletionDate
                  ? format(
                      new Date(currentJob.timeline.expectedCompletionDate),
                      "dd MMM yyyy"
                    )
                  : currentJob?.expectedCompletionDate
                  ? format(
                      new Date(currentJob.expectedCompletionDate),
                      "dd MMM yyyy"
                    )
                  : "-"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State for Karigars */}
      {(jobLoading.availableKarigars || fetchingKarigars) && (
        <Card>
          <CardContent className="p-8 text-center">
            <LoadingSpinner />
            <p className="text-muted-foreground mt-2">
              Loading available karigars...
            </p>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {recommendations &&
        !jobLoading.availableKarigars &&
        !fetchingKarigars && (
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="h-5 w-5 text-blue-600" />
                Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {recommendations.bestProductKarigar && (
                  <div
                    className="cursor-pointer hover:bg-blue-100 p-2 rounded"
                    onClick={() =>
                      setSelectedKarigar(recommendations.bestProductKarigar)
                    }
                  >
                    <p className="text-muted-foreground">
                      Best Product Karigar
                    </p>
                    <p className="font-medium">
                      {recommendations.bestProductKarigar.name}
                    </p>
                  </div>
                )}
                {recommendations.primaryKarigar && (
                  <div
                    className="cursor-pointer hover:bg-blue-100 p-2 rounded"
                    onClick={() =>
                      setSelectedKarigar(recommendations.primaryKarigar)
                    }
                  >
                    <p className="text-muted-foreground">Primary Karigar</p>
                    <p className="font-medium">
                      {recommendations.primaryKarigar.name}
                    </p>
                  </div>
                )}
                {recommendations.fastestAvailable && (
                  <div
                    className="cursor-pointer hover:bg-blue-100 p-2 rounded"
                    onClick={() =>
                      setSelectedKarigar(recommendations.fastestAvailable)
                    }
                  >
                    <p className="text-muted-foreground">Fastest Available</p>
                    <p className="font-medium">
                      {recommendations.fastestAvailable.name}
                    </p>
                  </div>
                )}
                {recommendations.highestRated && (
                  <div
                    className="cursor-pointer hover:bg-blue-100 p-2 rounded"
                    onClick={() =>
                      setSelectedKarigar(recommendations.highestRated)
                    }
                  >
                    <p className="text-muted-foreground">Highest Rated</p>
                    <p className="font-medium">
                      {recommendations.highestRated.name}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

      {/* ========== PRODUCT'S KARIGARS (SHOWN FIRST) ========== */}
      {!jobLoading.availableKarigars &&
        !fetchingKarigars &&
        productKarigars.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-amber-500" />
              <h2 className="text-lg font-semibold">
                Recommended Karigars for This Product
              </h2>
              <Badge variant="secondary">{productKarigars.length}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {productKarigars.map((karigar) =>
                renderKarigarCard(karigar, true)
              )}
            </div>
          </div>
        )}

      {/* ========== OTHER KARIGARS (SHOWN AFTER) ========== */}
      {!jobLoading.availableKarigars &&
        !fetchingKarigars &&
        otherKarigars.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-blue-500" />
              <h2 className="text-lg font-semibold">
                Other Available Karigars
              </h2>
              <Badge variant="secondary">{otherKarigars.length}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {otherKarigars.map((karigar) =>
                renderKarigarCard(karigar, false)
              )}
            </div>
          </div>
        )}

      {/* Show allKarigars if productKarigars and otherKarigars are empty but allKarigars has data */}
      {!jobLoading.availableKarigars &&
        !fetchingKarigars &&
        productKarigars.length === 0 &&
        otherKarigars.length === 0 &&
        allKarigars.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-blue-500" />
              <h2 className="text-lg font-semibold">Available Karigars</h2>
              <Badge variant="secondary">{allKarigars.length}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allKarigars.map((karigar) => renderKarigarCard(karigar, false))}
            </div>
          </div>
        )}

      {/* No Karigars Found */}
      {!jobLoading.availableKarigars &&
        !fetchingKarigars &&
        productKarigars.length === 0 &&
        otherKarigars.length === 0 &&
        allKarigars.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">No available karigars found</p>
              <p className="text-muted-foreground mt-1">
                {product?.type
                  ? `No karigars available for ${product.type} ${
                      product.subType || ""
                    } work`
                  : "No karigars available for this job"}
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={handleRefreshKarigars}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}

      {/* Assignment Form (Fixed Bottom) */}
      {selectedKarigar && (
        <Card className="sticky bottom-4 shadow-lg border-primary">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1">
                <p className="text-sm text-muted-foreground mb-1">
                  Selected Karigar
                </p>
                <p className="font-semibold">
                  {selectedKarigar.name} ({selectedKarigar.karigarCode})
                </p>
              </div>

              <div className="w-48">
                <Label htmlFor="expectedDate">Expected Completion</Label>
                <Input
                  id="expectedDate"
                  type="date"
                  value={expectedDate}
                  onChange={(e) => setExpectedDate(e.target.value)}
                  min={format(new Date(), "yyyy-MM-dd")}
                />
              </div>

              <div className="flex-1 min-w-[200px]">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Input
                  id="notes"
                  placeholder="Assignment notes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              <Button onClick={() => setShowConfirmDialog(true)}>
                Assign Karigar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Assignment</DialogTitle>
            <DialogDescription>
              Are you sure you want to assign this job to{" "}
              {selectedKarigar?.name}?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Karigar:</span>
              <span className="font-medium">{selectedKarigar?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Expected Completion:
              </span>
              <span className="font-medium">
                {expectedDate
                  ? format(new Date(expectedDate), "dd MMM yyyy")
                  : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Estimated Time:</span>
              <span className="font-medium">
                {selectedKarigar?.estimation?.estimatedDays || "-"} days
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={assigning}>
              {assigning ? "Assigning..." : "Confirm Assignment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobAssignmentPage;
